<template>
    <p-secure require-admin>
    <p-form @submit='onSubmit'>
        <div class='container-fluid content-wrapper' v-if='user'>
            <b-row class='content-heading'>
                <b-col>
                    <div>{{ isNew ? 'Add' : 'Edit' }} User</div>
                </b-col>
                <b-col class='text-end'>
                    <b-button
                        v-if='!isNew'
                        class='me-2'
                        hidden
                        variant='danger'
                        @click='deleteUser'
                    >
                        Delete
                    </b-button>
                    <b-button type='submit' variant='primary'
                        >Save</b-button
                    >
                </b-col>
            </b-row>
            <p-card v-if="isExternalUser">
                <b-row>
                    <b-col cols='12' lg='6'>
                        <b-form-group label="External SSO Providers" label-class="h3">
                            <b-form-input plaintext v-for="(login, index) in this.user.userLogins" :key="index" :value="login.loginProvider"></b-form-input>
                        </b-form-group>
                    </b-col>
                </b-row>
            </p-card>
            <p-card>
                <b-row>
                    <b-col cols='12' lg='6'>
                        <b-form-group>
                            <slot name="label"><div class="pb-2">Account Type</div></slot>
                            <b-form-radio-group v-model="user.isApiUser">
                                <b-form-radio :value=false>Application User</b-form-radio>
                                <b-form-radio :value=true>API Key</b-form-radio>
                            </b-form-radio-group>
                        </b-form-group>
                        <p-input label='User Name'
                                 v-model='user.userName'
                                 rules='required|max:256|valid_username'
                                 :disabled='!isNew' />
                        <p-input label='Display Name'
                                 v-model='user.displayName'
                                 rules='required|max:75' />
                        <p-input label='Email'
                                 v-model='user.email'
                                 rules='required|email|max:254' />
                        <p-checkbox label='Is Active?'
                                    v-model='user.isActive' />

                        <div v-if='!user.isApiUser'>
                            <p-checkbox label='2FA Enabled?'
                                        v-model='user.twoFactorEnabled' />
                            <div v-if='!editPassword' class='text-end'>
                                <b-link href="#" @click.prevent='showPassword'>Set Password</b-link>
                            </div>

                            <template v-if='editPassword'>
                                <p-input label='Password'
                                         v-model='user.password'
                                         type='password'
                                         vid='password'
                                         rules='required|min:8|strong_password' />
                                <p-input label='Confirm Password'
                                         v-model='confirmPassword'
                                         type='password'
                                         rules='required|confirmed:@password' />
                                <h5 class='text-muted pt-1'>
                                    Passwords must be at least 8 characters and
                                    contain at least: 1 letter and 1 number
                                </h5>
                            </template>
                        </div>
                        <div v-if="user.isApiUser">
                            <br>
                            <b-row align-v="end">
                                <b-col xs="12" xl="8">
                                    <p-input label='Api Key'
                                             v-model="user.apiKey"
                                             rules='required'
                                             disabled />
                                </b-col>
                                <b-col class="pb-3" cols="4">
                                    <b-button block variant="primary" @click="getNewApiKey">Generate</b-button>
                                </b-col>
                            </b-row>
                        </div>
                    </b-col>
                </b-row>
            </p-card>
            <p-card>
                <b-row>
                    <b-col>
                        <h3>Security Roles</h3>
                        <p>User will need to log out and back in for changes to take effect.  Unassigned role will be automatically assigned if no other roles are assigned.</p>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-form-checkbox-group v-model="selectedRoles" :options="roles" value-field="id" text-field="name" stacked >
                        </b-form-checkbox-group>                          
                    </b-col>
                </b-row>

            </p-card>

            <b-row>
                <b-col class=' mb-3 text-center'>
                    <b-button variant='primary' type='submit'>
                        Save
                    </b-button>
                </b-col>
            </b-row>
            
        </div>
    </p-form>
    </p-secure>
</template>

<script>
import axios from 'axios';
import NavigationGuard from '@/components/mixins/NavigationGuard.js';

export default{
    mixins: [NavigationGuard],
    props: {
        id: String        
    },
    data() {
        return {
            user: null,
            confirmPassword: '',
            editPasswordClicked: false,
            roles: [],
            selectedRoles: []
        };
    },
    watch: {
        user: NavigationGuard.$watcher,
        selectedRoles: {
            deep: true,
            handler: function(newVal) {
                // Retain NavigationGuard dirty-tracking on role changes (this key
                // replaced `selectedRoles: NavigationGuard.$watcher`).
                NavigationGuard.$watcher.handler.call(this);

                const unassignedRole = this.roles.find(r => r.name === 'Unassigned');

                // Remove Unassigned role if any other roles are selected
                if (unassignedRole && newVal.includes(unassignedRole.id) && newVal.length > 1) {
                    this.$nextTick(() => {
                        this.selectedRoles = newVal.filter(id => id !== unassignedRole.id);
                    });
                }
                // Default to Unassigned if no roles are selected
                else if ((!newVal || newVal.length === 0) && unassignedRole) {
                    this.$nextTick(() => {
                        this.selectedRoles = [unassignedRole.id];
                    });
                }
            }
        },
        $route: function(to, from) {
            if (to !== from) {
                this.loadData();
            }
        }
    },
    computed: {
        isNew() {
            return this.id === 'new';
        },
        isExternalUser() {
            return this.user?.userLogins?.length > 0;
        },
        editPassword() {
            return this.isNew || this.editPasswordClicked;
        }
    },
    methods: {
        onSubmit() {
            if(!this.selectedRoles || this.selectedRoles.length == 0){
                this.$bvModal.msgBoxOk('Please select at least one security role.');
                return;
            }

            //if Admin role is being added, add a confirmation to make sure they want to do this
            var adminRole = this.roles.find(r => r.name == 'Admin');
            if(adminRole && (this.selectedRoles.includes(adminRole.id) && !this.user.roles.includes(adminRole.id))){
                this.$bvModal
                    .msgBoxConfirm('You are making this user an Administrator that has unrestricted access to all modules, including the ability to modify other user permissions. Are you sure?')
                    .then(value => {
                        if(value){
                            this.saveUser();
                        }
                    })
            }
            else{
                this.saveUser();
            }            
        },
        saveUser(){
            this.user.roles = this.selectedRoles;
            axios.post('users', this.user).then(response => {
                this.dataSaved();
                if (this.isNew) {
                    this.$router.push('/users/' + response.data);
                }
                this.$appSuccess(
                        `User '${this.user.userName}' saved successfully.`
                    )
            });
        },
        getNewApiKey(){
            axios.get('users/newapikey').then(res => {
                this.user.apiKey = res.data;
            })
        },
        showPassword() {
            this.editPasswordClicked = true;
        },
        deleteUser() {
            this.$bvModal
                .msgBoxConfirm('Are you sure you want to delete this user?')
                .then(value => {
                    if (value) {
                        axios.post('users/delete', this.user).then(resp => {
                            if (resp) {
                                this.dataSaved();
                                this.$appSuccess(
                                        `${this.user.userName} deleted successfully.`
                                    )
                                this.$router.push('/users');
                            }
                        });
                    }
                });
        },
        loadData() {
            Promise.all([
                axios.get('roles'),
                this.isNew 
                    ? axios.get('users/new') 
                    : axios.get('users', { params: { id: this.id } })
            ]).then(([roles, user]) => {
                this.roles = roles.data.sort(function(a, b) {
                    if(a.name < b.name) { return -1; }
                    if(a.name > b.name) { return 1; }
                    return 0;
                });
                
                this.user = user.data;
                this.selectedRoles = user.data.roles;
                
                if (!this.isNew) {
                    this.dataLoaded();
                }
            });
        }
    },
    mounted: function() {
        this.loadData();
    }
};
</script>
